import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import parentService from '../../services/parentService';
import { FileText, Clock, CheckCircle, Warning, ArrowUp } from '@phosphor-icons/react';

const ParentAssignmentsSection = ({ childId, academicTerms = [], selectedAcademicTerm: propSelectedTerm = null }) => {
  const [assignments, setAssignments] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [expandedAssignment, setExpandedAssignment] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedAcademicTerm, setSelectedAcademicTerm] = useState(propSelectedTerm);
  const [activeTab, setActiveTab] = useState('overview');

  const loadAssignmentsAndMetrics = React.useCallback(async () => {
    await Promise.all([loadAssignments(), loadMetrics()]);
  }, [childId, selectedAcademicTerm]);

  useEffect(() => {
    if (childId) {
      loadAssignmentsAndMetrics();
    }
  }, [childId, selectedAcademicTerm, loadAssignmentsAndMetrics]);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const data = await parentService.getChildAssignments(childId, selectedAcademicTerm);
      setAssignments(data.assignments || []);
    } catch (error) {
      console.error('Failed to load assignments:', error);
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    setMetricsLoading(true);
    try {
      const data = await parentService.getChildAssignmentMetrics(childId, selectedAcademicTerm);
      if (data.error) {
        console.warn('Metrics error:', data.error);
        setMetrics(null);
      } else {
        setMetrics(data);
      }
    } catch (error) {
      console.error('Failed to load metrics:', error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  };

  const getAssignmentStatus = (assignment) => {
    if (assignment.submitted_at) {
      return assignment.graded ? 'graded' : 'submitted';
    }
    return new Date() > new Date(assignment.due_date) ? 'overdue' : 'pending';
  };

  const getStatusColor = (status) => {
    const colors = { pending: '#F59E0B', submitted: '#3B82F6', graded: '#10B981', overdue: '#EF4444' };
    return colors[status] || '#6B7280';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock size={18} />;
      case 'submitted':
      case 'graded':
        return <CheckCircle size={18} />;
      case 'overdue':
        return <Warning size={18} />;
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredAssignments = filter === 'all' ? assignments : assignments.filter(a => getAssignmentStatus(a) === filter);

  const stats = {
    total: assignments.length,
    pending: assignments.filter(a => getAssignmentStatus(a) === 'pending').length,
    submitted: assignments.filter(a => getAssignmentStatus(a) === 'submitted').length,
    graded: assignments.filter(a => getAssignmentStatus(a) === 'graded').length,
    overdue: assignments.filter(a => getAssignmentStatus(a) === 'overdue').length,
  };

  const MetricCard = ({ title, value, unit, grade, trend, status, subText }) => (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '16px',
      border: '1px solid #e5e7eb',
      textAlign: 'center',
      minHeight: '140px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <div>
        <h5 style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', margin: '0 0 12px 0', textTransform: 'uppercase' }}>
          {title}
        </h5>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '4px' }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#1f2937' }}>{value}</div>
          {unit && <div style={{ fontSize: '16px', color: '#6b7280' }}>{unit}</div>}
        </div>
        {grade && <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginTop: '8px' }}>Grade {grade}</div>}
      </div>
      <div>
        {trend && <div style={{ fontSize: '12px', color: '#059669', marginTop: '8px', fontWeight: '500' }}>{trend}</div>}
        {status && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>{status}</div>}
        {subText && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>{subText}</div>}
      </div>
    </div>
  );

  const renderAssignmentCard = (assignment) => {
    const status = getAssignmentStatus(assignment);
    const isExpanded = expandedAssignment === assignment.id;

    return (
      <div
        key={assignment.id}
        className={`assignment-card ${status}`}
        style={{
          borderLeft: `4px solid ${getStatusColor(status)}`,
          marginBottom: '16px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #e5e7eb',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        onClick={() => setExpandedAssignment(isExpanded ? null : assignment.id)}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <h4 style={{ marginBottom: '4px', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
              {assignment.title}
            </h4>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
              {assignment.subject_name} • By {assignment.teacher_name}
            </p>
          </div>
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '20px',
            backgroundColor: `${getStatusColor(status)}20`,
            color: getStatusColor(status),
            fontSize: '12px',
            fontWeight: '600',
            whiteSpace: 'nowrap'
          }}>
            {getStatusIcon(status)}
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '12px', fontSize: '12px', color: '#6b7280' }}>
          <div><span style={{ fontWeight: '500', color: '#374151' }}>Posted:</span> {formatDate(assignment.created_at)}</div>
          <div><span style={{ fontWeight: '500', color: '#374151' }}>Due:</span> {formatDate(assignment.due_date)}</div>
          {assignment.submitted_at && <div><span style={{ fontWeight: '500', color: '#374151' }}>Submitted:</span> {formatDate(assignment.submitted_at)}</div>}
        </div>

        {isExpanded && (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '12px' }}>
            {assignment.description && (
              <div style={{ marginBottom: '16px' }}>
                <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Description</h5>
                <p style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.6' }}>{assignment.description}</p>
              </div>
            )}

            {assignment.graded && (
              <div style={{ backgroundColor: '#f0fdf4', borderRadius: '6px', padding: '12px', marginBottom: '16px', borderLeft: '3px solid #10B981' }}>
                <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#15803d', marginBottom: '8px' }}>Grade</h5>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '8px' }}>
                  <span>Score: <strong>{assignment.score}/{assignment.max_score}</strong></span>
                  <span>Percentage: <strong>{assignment.percentage}%</strong></span>
                </div>
                {assignment.feedback && (
                  <div style={{ padding: '8px', backgroundColor: '#fff', borderRadius: '4px', fontSize: '12px', color: '#4b5563' }}>
                    <strong>Feedback:</strong> {assignment.feedback}
                  </div>
                )}
              </div>
            )}

            {status === 'submitted' && (
              <div style={{ backgroundColor: '#eff6ff', borderRadius: '6px', padding: '12px', borderLeft: '3px solid #3B82F6' }}>
                <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#1e40af', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={16} /> Submitted</h5>
                <p style={{ fontSize: '12px', color: '#1e40af', margin: 0 }}>
                  Assignment was submitted on {formatDate(assignment.submitted_at)}. Awaiting grade.
                </p>
              </div>
            )}

            {status === 'overdue' && (
              <div style={{ backgroundColor: '#fef2f2', borderRadius: '6px', padding: '12px', borderLeft: '3px solid #EF4444', color: '#991b1b' }}>
                <h5 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                  <Warning size={16} style={{ display: 'inline-block', marginRight: '6px' }} />
                  Assignment is Overdue
                </h5>
                <p style={{ fontSize: '12px', margin: 0 }}>Due date: {formatDate(assignment.due_date)}. Please contact the teacher if more time is needed.</p>
              </div>
            )}

            {status === 'pending' && (
              <div style={{ backgroundColor: '#fffbeb', borderRadius: '6px', padding: '12px', borderLeft: '3px solid #F59E0B' }}>
                <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#78350f', marginBottom: '4px' }}>
                  <Clock size={16} style={{ display: 'inline-block', marginRight: '6px' }} />
                  Pending Submission
                </h5>
                <p style={{ fontSize: '12px', color: '#78350f', margin: 0 }}>Due: {formatDate(assignment.due_date)}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading && metricsLoading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading assignments...</div>;
  }

  return (
    <div className="parent-assignments-section">
      <div className="assignments-header">
        <h3><FileText size={24} /> Child's Assignments</h3>
        <p>{assignments.length} assignment{assignments.length !== 1 ? 's' : ''}</p>
      </div>

      {academicTerms && academicTerms.length > 0 && (
        <div style={{ marginBottom: '20px', marginTop: '16px' }}>
          <select
            value={selectedAcademicTerm || ''}
            onChange={(e) => setSelectedAcademicTerm(e.target.value || null)}
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd', width: '100%', maxWidth: '300px', color: '#111827', backgroundColor: '#ffffff' }}
          >
            <option value="">All Terms</option>
            {academicTerms.map(term => React.createElement(
              'option',
              { key: term.id, value: term.id },
              `${term.term} - ${term.academic_year}`
            ))}
          </select>
        </div>
      )}

      {assignments.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
          <FileText size={48} weight="duotone" style={{ color: '#d1d5db', marginBottom: '16px' }} />
          <h3>No Assignments Yet</h3>
          <p>Assignments will appear here when teachers assign them</p>
        </div>
      ) : (
        <>
          <div style={{
            display: 'flex',
            gap: '0',
            marginBottom: '24px',
            borderBottom: '2px solid #e5e7eb',
            overflow: 'auto'
          }}>
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'bySubject', label: 'By Subject' },
              { id: 'types', label: 'Assessment Types' },
              { id: 'trends', label: 'Trends' },
              { id: 'details', label: 'All Assignments' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '12px 16px',
                  border: 'none',
                  background: 'none',
                  fontSize: '14px',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  color: activeTab === tab.id ? '#3B82F6' : '#6b7280',
                  borderBottom: activeTab === tab.id ? '3px solid #3B82F6' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <>
              {metrics && !metricsLoading ? (
                <div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginBottom: '32px'
                  }}>
                    <MetricCard
                      title="Performance Index"
                      value={metrics.assignment_performance_index?.percentage || 0}
                      unit="%"
                      grade={metrics.assignment_performance_index?.grade}
                      trend={metrics.assignment_performance_index?.trend}
                      status={metrics.assignment_performance_index?.status}
                    />
                    <MetricCard
                      title="Completion Rate"
                      value={metrics.completion_and_punctuality?.completion_rate || 0}
                      unit="%"
                      subText={`${metrics.completion_and_punctuality?.submitted_count || 0}/${metrics.completion_and_punctuality?.total_count || 0} submitted`}
                    />
                    <MetricCard
                      title="On-Time Submissions"
                      value={metrics.completion_and_punctuality?.punctuality_rate || 0}
                      unit="%"
                      subText={`${metrics.completion_and_punctuality?.late_count || 0} late submissions`}
                      status={metrics.completion_and_punctuality?.punctuality_rate < 70
                        ? <><Warning size={14} /> Needs improvement</>
                        : <><CheckCircle size={14} /> Good</>}
                    />
                    <MetricCard
                      title="Outstanding"
                      value={metrics.completion_and_punctuality?.outstanding || 0}
                      unit="pending"
                      status={metrics.completion_and_punctuality?.outstanding > 0 ? 'Pending submission' : <><CheckCircle size={14} /> All submitted</>}
                    />
                  </div>

                  {metrics.progress_trend && (
                    <div style={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      padding: '16px',
                      border: '1px solid #e5e7eb',
                      marginBottom: '24px'
                    }}>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                        <ArrowUp size={18} style={{ display: 'inline-block', marginRight: '8px' }} />
                        Progress Trend
                      </h4>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '12px',
                        marginBottom: '16px'
                      }}>
                        {['window_1', 'window_2', 'window_3', 'window_4'].map((window, idx) => (
                          <div key={window} style={{
                            padding: '12px',
                            backgroundColor: metrics.progress_trend.window_averages[window] ? '#f0fdf4' : '#f3f4f6',
                            borderRadius: '6px',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Week {idx + 1}</div>
                            <div style={{
                              fontSize: '18px',
                              fontWeight: '700',
                              color: metrics.progress_trend.window_averages[window] ? '#059669' : '#9ca3af'
                            }}>
                              {metrics.progress_trend.window_averages[window] ? `${metrics.progress_trend.window_averages[window]}%` : '-'}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#eff6ff',
                        borderRadius: '6px',
                        borderLeft: '3px solid #3B82F6'
                      }}>
                        <span style={{ fontSize: '13px', color: '#1e40af' }}>
                          <strong>{metrics.progress_trend.direction}</strong>
                          {metrics.progress_trend.change_percentage !== 0 && (
                            <> • {metrics.progress_trend.change_percentage > 0 ? '+' : ''}{metrics.progress_trend.change_percentage}% change</>
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: '24px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>Recent Assignments</h4>
                    <div className="assignments-list">
                      {assignments.slice(0, 5).map(renderAssignmentCard)}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                  {metricsLoading ? 'Loading metrics...' : 'Metrics not available'}
                </div>
              )}
            </>
          )}

          {activeTab === 'bySubject' && metrics && (
            <div>
              {Object.keys(metrics.subject_performance || {}).length > 0 ? (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {Object.entries(metrics.subject_performance || {}).map(([subject, data]) => (
                    <div key={subject} style={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      padding: '16px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <h5 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>{subject}</h5>
                        <p style={{ margin: '4px 0', fontSize: '12px', color: '#6b7280' }}>
                          {data.count} grade{data.count !== 1 ? 's' : ''} • Grade {data.grade} ({data.description})
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>{data.average_score}%</div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{data.trend}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No subject data available</div>
              )}
            </div>
          )}

          {activeTab === 'types' && metrics && (
            <div>
              {Object.keys(metrics.assessment_type_breakdown || {}).length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px'
                }}>
                  {Object.entries(metrics.assessment_type_breakdown || {}).map(([type, data]) => (
                    <div key={type} style={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      padding: '16px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <h5 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'capitalize' }}>
                        {type}
                      </h5>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
                        {data.average}%
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                        {data.count} assignment{data.count !== 1 ? 's' : ''}
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '11px',
                        color: '#9ca3af',
                        borderTop: '1px solid #e5e7eb',
                        paddingTop: '8px'
                      }}>
                        <span>Best: {data.best}%</span>
                        <span>Lowest: {data.lowest}%</span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#059669', marginTop: '8px', fontWeight: '500' }}>
                        {data.trend}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No assessment type data available</div>
              )}
            </div>
          )}

          {activeTab === 'trends' && metrics && metrics.progress_trend && (
            <div style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '24px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                Performance Progression Through Term
              </h4>

              <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
                {['window_1', 'window_2', 'window_3', 'window_4'].map((window, idx) => {
                  const value = metrics.progress_trend.window_averages[window];
                  return (
                    <div key={window}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                          Week {idx * 2 + 1} - Week {(idx + 1) * 2}
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                          {value ? `${value}%` : 'No data'}
                        </span>
                      </div>
                      <div style={{
                        height: '8px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: value ? `${value}%` : '0%',
                          backgroundColor: value >= 80 ? '#10B981' : value >= 60 ? '#F59E0B' : '#EF4444',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{
                padding: '16px',
                backgroundColor: '#f0fdf4',
                borderRadius: '6px',
                borderLeft: '3px solid #10B981'
              }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#15803d' }}>
                  <strong>Trend: {metrics.progress_trend.direction}</strong>
                  {metrics.progress_trend.change_percentage !== 0 && (
                    <> • {metrics.progress_trend.change_percentage > 0 ? '+' : ''}{metrics.progress_trend.change_percentage}% overall change</>
                  )}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <>
              <div className="assignments-stats-grid" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
                gap: '12px', 
                marginBottom: '24px' 
              }}>
                {[
                  { key: 'all', label: 'Total', count: stats.total, color: '#3B82F6' },
                  { key: 'pending', label: 'Pending', count: stats.pending, color: '#F59E0B' },
                  { key: 'submitted', label: 'Submitted', count: stats.submitted, color: '#3B82F6' },
                  { key: 'graded', label: 'Graded', count: stats.graded, color: '#10B981' },
                  ...(stats.overdue > 0 ? [{ key: 'overdue', label: 'Overdue', count: stats.overdue, color: '#EF4444' }] : [])
                ].map(stat => (
                  <div
                    key={stat.key}
                    onClick={() => setFilter(stat.key)}
                    style={{
                      padding: '14px',
                      borderRadius: '8px',
                      backgroundColor: filter === stat.key ? `${stat.color}15` : '#f3f4f6',
                      border: filter === stat.key ? `2px solid ${stat.color}` : '1px solid #e5e7eb',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ fontSize: '20px', fontWeight: '700', color: stat.color }}>{stat.count}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="assignments-list">
                {filteredAssignments.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                    <FileText size={48} weight="duotone" style={{ color: '#d1d5db', marginBottom: '16px' }} />
                    <h3>No {filter !== 'all' ? filter : ''} assignments</h3>
                  </div>
                ) : (
                  filteredAssignments.map(renderAssignmentCard)
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ParentAssignmentsSection;
